import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { SBOM_PORTAL } from '../../../../../../shared/featureFlags/flagNames';
import { Action, ResourceType } from '../../../../../../typings/permissions';
import { window } from '../../../../../globals';
import { useToggle } from '../../../../../hooks';
import * as reportGeneration from '../../../../../redux/reportGeneration';
import {
  BUTTON_SIZE,
  BUTTON_TYPE,
  FeatureFlagRequired,
  Icon,
  icons,
  NavigationButton,
} from '../../../../generic';
import Button from '../../../../generic/Button';
import Flex from '../../../../generic/Flex';
import { getDownloadFixPlansReportURL, getDownloadVulnerabilityReportURL } from '../releaseGroupLevel/utils';
import { ReportCategories } from '../utils';
import type { ProjectGroupContext, ReportCategory, ReportExclusionOptions } from '../../shared/types';
import type {
  DependencyMetadataFilterOption,
  FormatName,
  IncludeFilterOption,
  IncludeFixPlanFilterOption,
} from '../shared/types';

import EmailDialog from './EmailDialog';
import PublishReportButton from './PublishReportButton';

type Props = {
  isDisabled?: boolean;
  reportType: ReportCategory;
  format: FormatName | null;
  includeOptions: IncludeFilterOption[];
  dependencyMetadataOptions: DependencyMetadataFilterOption[];
  fixPlanOptions: IncludeFixPlanFilterOption[];
  excludeOptions?: ReportExclusionOptions;
  authorName?: string;
  authorEmail?: string;
  supplier?: string;
};

const ProjectGroupActionButtons: VoidFunctionComponent<Props> = ({
  format,
  reportType,
  includeOptions,
  dependencyMetadataOptions,
  fixPlanOptions,
  excludeOptions,
  isDisabled,
  authorName,
  authorEmail,
  supplier,
}: Props) => {
  const isVulnerabilityReport = reportType === ReportCategories.Vulnerability;
  const isFixPlanReport = reportType === ReportCategories.RemediationGuidance;
  const hasPermissionToEditPortalSettings = window.hasPermission({
    resourceType: ResourceType.OrganizationPortalReports,
    action: Action.Manage,
  });
  const reportContext = useSelector(reportGeneration.selectors.getReportContext);
  const [isToggled, onToggle] = useToggle();

  // Email option not supported for either Vulnerability or Fix Plan
  const shouldShowEmailButton = !isFixPlanReport && !isVulnerabilityReport;

  return (
    <Flex gap="4px" justify="flex-end">
      {isToggled && (
        <EmailDialog
          onClose={onToggle}
          format={format}
          reportType={reportType}
          includeOptions={includeOptions}
          dependencyMetadataOptions={dependencyMetadataOptions}
          excludeOptions={excludeOptions}
          authorName={authorName}
          authorEmail={authorEmail}
          supplier={supplier}
        />
      )}
      {shouldShowEmailButton && (
        <Button onClick={onToggle} size={BUTTON_SIZE.LARGE} type={BUTTON_TYPE.SECONDARY} isDisabled={isDisabled}>
          <Icon icon={icons.envelopeRegular} /> Email
        </Button>
      )}
      {
        isVulnerabilityReport && (
          <NavigationButton
            isDisabled={isDisabled}
            openInNewTab
            size={BUTTON_SIZE.LARGE}
            to={
              getDownloadVulnerabilityReportURL({
                groupId: (reportContext as ProjectGroupContext).groupId,
                releaseId: (reportContext as ProjectGroupContext).releaseId,
                includeOptions,
                dependencyMetadataOptions,
                excludeOptions,
              }) || ''
            }
          >
            <Icon icon={icons.downloadRegular} /> Download
          </NavigationButton>
        )
      }
      {
        isFixPlanReport && (
          <NavigationButton
            isDisabled={isDisabled}
            openInNewTab
            size={BUTTON_SIZE.LARGE}
            to={
              getDownloadFixPlansReportURL({
                groupId: (reportContext as ProjectGroupContext).groupId,
                releaseId: (reportContext as ProjectGroupContext).releaseId,
                format,
                fixPlanOptions,
              }) || ''
            }
          >
            <Icon icon={icons.downloadRegular} /> Download
          </NavigationButton>
        )
      }
      <FeatureFlagRequired flagName={SBOM_PORTAL}>
        {hasPermissionToEditPortalSettings && (
          <PublishReportButton
            isDisabled={isDisabled}
            format={format}
            reportType={reportType}
            includeOptions={includeOptions}
            dependencyMetadataOptions={dependencyMetadataOptions}
            excludeOptions={excludeOptions}
            authorName={authorName}
            authorEmail={authorEmail}
            supplier={supplier}
          />
        )}
      </FeatureFlagRequired>
    </Flex>
  );
};

export default ProjectGroupActionButtons;
